import {Map} from 'immutable';

import {formatKpiValue, KpiTarget, KpiTargetId, KpiTargetNumericType} from '@store/@types/kpi';
import {Metric, MetricParams, MetricRange} from '@store/@types/metric';
import formatSecondsShort from 'utils/formatSecondsShort';

export const getAxisValue = (metricRange: MetricRange, value: number): number => {
  const range = metricRange.MAX - metricRange.MIN;
  // Don't divide by 0
  if (range === 0) return 0;

  const distanceFromMin = value - metricRange.MIN;
  // find the proportional value where this value falls in the range
  // then multiply by 100 to find it on a 0 - 100 basis
  // 100 * 20 / 100 = 20
  // 100 * 53 / 344 = 15
  return Math.round(100 * (distanceFromMin / range));
};

const percentFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
}).format;

export const formatMetric = (
  value: number,
  metric: MetricParams,
  kpisById: Map<KpiTargetId, KpiTarget>
): string => {
  switch (metric.metric) {
    case Metric.CALL_SCORING:
    case Metric.CALL_SCORING_SKILLS:
    case Metric.ENGAGEMENT:
      return percentFormat(value / 100);
    case Metric.CALL_AVG_DURATION:
    case Metric.CALL_TOTAL_DURATION:
      return formatSecondsShort(value, false, false, true);
    case Metric.KPI_OBSERVATION:
      const numericType = kpisById.get(parseInt(metric.context!, 10))?.numericType;
      // TODO validate this is what we want, or if its needed
      if (!numericType) {
        return '--';
      }
      return formatKpiValue(numericType, value, undefined, true);
    default:
      return Math.round(value) + '';
  }
};

const FREE_SPACE = 0.2; // 20%
const add20PercentMax = (range: MetricRange, zeroMin: boolean) => {
  const rangeLength = range.MAX - range.MIN;
  const max = Math.ceil(range.MAX + FREE_SPACE * rangeLength);
  const min = zeroMin ? 0 : Math.floor(range.MIN);

  return {MAX: max, MIN: min};
};

export const prepareRange = (
  range: MetricRange,
  metricParams: MetricParams,
  kpisById: Map<KpiTargetId, KpiTarget>
): MetricRange => {
  switch (metricParams.metric) {
    case Metric.ENGAGEMENT:
    case Metric.AGGREGATED_SKILL:
    case Metric.CALL_SCORING:
    case Metric.CALL_SCORING_SKILLS:
      return {MAX: 100, MIN: 0};
    case Metric.KPI_OBSERVATION:
      const goal = kpisById.get(parseInt(metricParams.context!, 10));
      const max = Math.max(range.MAX, goal?.value ?? range.MAX);
      const min = Math.min(range.MIN, goal?.value ?? range.MIN);
      const rangeLength = max - min;
      const padding = rangeLength * FREE_SPACE;
      const zeroMinType =
        goal?.numericType === KpiTargetNumericType.PERCENTAGE ||
        goal?.numericType === KpiTargetNumericType.CURRENCY;

      // Special case to avoid rendering near the graph edge
      if (max === min) {
        return {MAX: max * 2, MIN: zeroMinType ? 0 : max / 2};
      }

      if (zeroMinType) {
        return {
          MAX: max + padding,
          MIN: Math.min(min, 0),
        };
      }

      return {
        MAX: max + padding,
        MIN: min - padding,
      };
    case Metric.CALL_TOTAL_DURATION:
      return add20PercentMax(range, true);
    default:
      return add20PercentMax(range, false);
  }
};
